// Stage 74 — the single app-wide side-panel manager.
//
// One panel instance lives in cos-app-shell; pages summon content through
// this singleton (the Stage 71 palette / Stage 72 overlay precedent — a
// module-level const, not a static `.instance`). The shell subscribes and
// re-renders the one <cos-side-panel> from this state.
//
// Lifecycle: open() sets the content + open; close() flips open=false so
// the exit transition plays, then clears the content after the animation
// so a closed panel holds no live body (e.g. a record-conversation's ws
// listener). Last-tab persists per contextKey in sessionStorage.

export interface OpenPanelOptions {
	title: string;
	width?: 'sm' | 'md' | 'lg';
	scrim?: boolean;
	tabs?: Array<{ id: string; label: string }>;
	initialTab?: string;
	/** sessionStorage key for last-tab persistence (Stage 71 precedent). */
	contextKey?: string;
	/** Body template for the active tab. */
	renderBody: (activeTab: string) => unknown;
	/** Optional footer template. */
	renderFooter?: () => unknown;
	/** Fired once when the panel is dismissed (Esc / scrim / close / route change). */
	onClose?: () => void;
}

export interface PanelState {
	open: boolean;
	opts?: OpenPanelOptions;
	activeTab: string;
}

const TAB_KEY_PREFIX = 'cos.panel-tab.';
// Matches the 220ms CSS slide-out + a little slack, so content clears only
// after the panel is visually gone.
const CLEAR_AFTER_MS = 260;

class SidePanelManager {
	private state: PanelState = { open: false, activeTab: '' };
	private listeners = new Set<() => void>();
	private clearTimer: ReturnType<typeof setTimeout> | null = null;

	open(opts: OpenPanelOptions): void {
		if (this.clearTimer) {
			clearTimeout(this.clearTimer);
			this.clearTimer = null;
		}
		const activeTab = this._resolveInitialTab(opts);
		this.state = { open: true, opts, activeTab };
		this.notify();
	}

	close(): void {
		if (!this.state.open) return;
		const onClose = this.state.opts?.onClose;
		this.state = { ...this.state, open: false };
		this.notify();
		onClose?.();
		// Clear the body after the slide-out so it doesn't linger.
		this.clearTimer = setTimeout(() => {
			this.clearTimer = null;
			if (!this.state.open) this.state = { open: false, activeTab: '' };
			this.notify();
		}, CLEAR_AFTER_MS);
	}

	setActiveTab(tabId: string): void {
		const opts = this.state.opts;
		if (!opts || tabId === this.state.activeTab) return;
		this.state = { ...this.state, activeTab: tabId };
		const key = opts.contextKey;
		if (key) {
			try {
				sessionStorage.setItem(TAB_KEY_PREFIX + key, tabId);
			} catch {
				/* private mode — last-tab just won't persist */
			}
		}
		this.notify();
	}

	subscribe(cb: () => void): () => void {
		this.listeners.add(cb);
		return () => this.listeners.delete(cb);
	}

	getState(): Readonly<PanelState> {
		return this.state;
	}

	private _resolveInitialTab(opts: OpenPanelOptions): string {
		const fallback = opts.initialTab ?? opts.tabs?.[0]?.id ?? '';
		if (!opts.contextKey) return fallback;
		try {
			const saved = sessionStorage.getItem(TAB_KEY_PREFIX + opts.contextKey);
			// Only honor a saved tab that's still valid for this panel.
			if (saved && (!opts.tabs || opts.tabs.some((t) => t.id === saved))) return saved;
		} catch {
			/* ignore */
		}
		return fallback;
	}

	private notify(): void {
		for (const cb of this.listeners) cb();
	}
}

/** The one app-wide side-panel manager. */
export const sidePanelManager = new SidePanelManager();
